import { DocumentNumberingPage } from '@/features/settings/DocumentNumberingPage';
const TasksPage = lazy(() =>
  import('@/features/tasks/TasksPage').then((m) => ({ default: m.TasksPage })),
);
const HolidayImportPage = lazy(() =>
  import('@/features/settings/HolidayImportPage').then((m) => ({ default: m.HolidayImportPage })),
);
const HolidaysPage = lazy(() =>
  import('@/features/settings/HolidaysPage').then((m) => ({ default: m.HolidaysPage })),
);
const HolidayFormPage = lazy(() =>
  import('@/features/settings/HolidaysPage').then((m) => ({ default: m.HolidayFormPage })),
);
const CandidateTrashPage = lazy(() =>
  import('@/features/settings/CandidateTrashPage').then((m) => ({ default: m.CandidateTrashPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const PreferencesPage = lazy(() =>
  import('@/features/settings/PreferencesPage').then((m) => ({ default: m.PreferencesPage })),
);
const CompanyIdentityPage = lazy(() =>
  import('@/features/settings/CompanyIdentityPage').then((m) => ({
    default: m.CompanyIdentityPage,
  })),
);
const AiEmailHistoryPage = lazy(() =>
  import('@/features/ai-emails/AiEmailHistoryPage').then((m) => ({
    default: m.AiEmailHistoryPage,
  })),
);
const AccountPage = lazy(() =>
  import('@/features/account/AccountPage').then((m) => ({ default: m.AccountPage })),
);
const PositioningPage = lazy(() =>
  import('@/features/positionings/PositioningPage').then((m) => ({ default: m.PositioningPage })),
);
const ActionEditPage = lazy(() =>
  import('@/features/actions/pages/ActionEditPage').then((m) => ({ default: m.ActionEditPage })),
);
import { AccessFormPage } from '@/features/access/AccessFormPage';
import { AccessPage } from '@/features/access/AccessPage';
import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import { AppLayout } from '@/app/layouts/AppLayout';
import { AuthLayout } from '@/app/layouts/AuthLayout';
import { FullscreenLayout } from '@/app/layouts/FullscreenLayout';
import { RootLayout } from '@/app/layouts/RootLayout';
import { SpinnerIcon } from '@/components/icons';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

import { RequireAuth } from './RequireAuth';
import { ROUTES } from './routes';

/**
 * Écran « Sociétés & contacts », chargé à la demande.
 *
 * `lazy` sort cette page du bundle initial : elle embarque deux tableaux, la
 * barre d'onglets et leurs requêtes, alors que la majorité des sessions
 * commencent — et beaucoup s'arrêtent — sur le tableau de bord. Ce qui n'est
 * pas ouvert n'est pas téléchargé.
 *
 * La page de connexion reste en import statique : c'est la toute première
 * affichée, la différer ajouterait un aller-retour réseau avant le premier
 * rendu utile.
 */
const CompaniesContactsPage = lazy(() =>
  import('@/features/companies/pages/CompaniesContactsPage').then((module) => ({
    default: module.CompaniesContactsPage,
  })),
);

const RequirementImportDetailPage = lazy(() =>
  import('@/features/requirement-imports/pages/RequirementImportDetailPage').then((m) => ({
    default: m.RequirementImportDetailPage,
  })),
);
const CvImportsPage = lazy(() =>
  import('@/features/cv-imports/pages/CvImportsPage').then((m) => ({ default: m.CvImportsPage })),
);

const CandidatesPage = lazy(() =>
  import('@/features/candidates/pages/CandidatesPage').then((module) => ({
    default: module.CandidatesPage,
  })),
);

const CandidateDetailPage = lazy(() =>
  import('@/features/candidates/pages/CandidateDetailPage').then((module) => ({
    default: module.CandidateDetailPage,
  })),
);
const CvContactCompletionPage = lazy(() =>
  import('@/features/cv-imports/pages/CvContactCompletionPage').then((m) => ({
    default: m.CvContactCompletionPage,
  })),
);
const CandidateCvUpdatePage = lazy(() =>
  import('@/features/cv-imports/pages/CandidateCvUpdatePage').then((m) => ({
    default: m.CandidateCvUpdatePage,
  })),
);

const CandidateCvPage = lazy(() =>
  import('@/features/candidate-cvs/CandidateCvPage').then((module) => ({
    default: module.CandidateCvPage,
  })),
);

const CompanyCreatePage = lazy(() =>
  import('@/features/companies/pages/CompanyCreatePage').then((module) => ({
    default: module.CompanyCreatePage,
  })),
);

const CompanyDetailPage = lazy(() =>
  import('@/features/companies/pages/CompanyDetailPage').then((module) => ({
    default: module.CompanyDetailPage,
  })),
);

const ContactCreatePage = lazy(() =>
  import('@/features/contacts/pages/ContactCreatePage').then((module) => ({
    default: module.ContactCreatePage,
  })),
);

const ContactDetailPage = lazy(() =>
  import('@/features/contacts/pages/ContactDetailPage').then((module) => ({
    default: module.ContactDetailPage,
  })),
);

const MissionFinancialPage = lazy(() =>
  import('@/features/adv/pages/MissionFinancialPage').then((module) => ({
    default: module.MissionFinancialPage,
  })),
);

const AdvPage = lazy(() =>
  import('@/features/adv/pages/AdvPage').then((module) => ({ default: module.AdvPage })),
);

/*
 * Un écran de saisie par type de document.
 *
 * Les cinq partagent un module, donc un fragment : ouvrir la création d'une
 * proposition charge aussi les quatre autres, ce qui est exactement ce qu'on
 * veut — on enchaîne rarement une seule pièce sur une affaire.
 */
const PropositionCreatePage = lazy(() =>
  import('@/features/adv/pages/DocumentCreatePages').then((module) => ({
    default: module.PropositionCreatePage,
  })),
);

const OrderCreatePage = lazy(() =>
  import('@/features/adv/pages/DocumentCreatePages').then((module) => ({
    default: module.OrderCreatePage,
  })),
);

const ContractCreatePage = lazy(() =>
  import('@/features/adv/pages/DocumentCreatePages').then((module) => ({
    default: module.ContractCreatePage,
  })),
);

const PvaCreatePage = lazy(() =>
  import('@/features/adv/pages/DocumentCreatePages').then((module) => ({
    default: module.PvaCreatePage,
  })),
);

const InvoiceCreatePage = lazy(() =>
  import('@/features/adv/pages/DocumentCreatePages').then((module) => ({
    default: module.InvoiceCreatePage,
  })),
);

const DocumentDetailPage = lazy(() =>
  import('@/features/adv/pages/DocumentDetailPage').then((module) => ({
    default: module.DocumentDetailPage,
  })),
);

const SequencesPage = lazy(() =>
  import('@/features/messaging/pages/SequencesPage').then((module) => ({
    default: module.SequencesPage,
  })),
);

const SequenceCreatePage = lazy(() =>
  import('@/features/messaging/pages/SequenceCreatePage').then((module) => ({
    default: module.SequenceCreatePage,
  })),
);

const SequenceEditPage = lazy(() =>
  import('@/features/messaging/pages/SequenceEditPage').then((module) => ({
    default: module.SequenceEditPage,
  })),
);

const SequenceDetailPage = lazy(() =>
  import('@/features/messaging/pages/SequenceDetailPage').then((module) => ({
    default: module.SequenceDetailPage,
  })),
);

const RunDetailPage = lazy(() =>
  import('@/features/messaging/pages/RunDetailPage').then((module) => ({
    default: module.RunDetailPage,
  })),
);

const ProspectionPage = lazy(() =>
  import('@/features/prospection/pages/ProspectionPage').then((module) => ({
    default: module.ProspectionPage,
  })),
);

const TimesheetsPage = lazy(() =>
  import('@/features/timesheets/pages/TimesheetsPage').then((module) => ({
    default: module.TimesheetsPage,
  })),
);

const TimesheetDetailPage = lazy(() =>
  import('@/features/timesheets/pages/TimesheetDetailPage').then((module) => ({
    default: module.TimesheetDetailPage,
  })),
);
const TimesheetSatisfactionSettingsPage = lazy(() =>
  import('@/features/timesheets/pages/TimesheetSatisfactionSettingsPage').then((module) => ({
    default: module.TimesheetSatisfactionSettingsPage,
  })),
);
const TimesheetSatisfactionPage = lazy(() =>
  import('@/features/timesheets/pages/TimesheetSatisfactionPage').then((module) => ({
    default: module.TimesheetSatisfactionPage,
  })),
);

const InterviewsPage = lazy(() =>
  import('@/features/actions/pages/InterviewsPage').then((module) => ({
    default: module.InterviewsPage,
  })),
);
const ActionsPage = lazy(() =>
  import('@/features/actions/pages/ActionsPage').then((module) => ({
    default: module.ActionsPage,
  })),
);

const ActionCreatePage = lazy(() =>
  import('@/features/actions/pages/ActionCreatePage').then((module) => ({
    default: module.ActionCreatePage,
  })),
);

const ProjectsPage = lazy(() =>
  import('@/features/projects/pages/ProjectsPage').then((module) => ({
    default: module.ProjectsPage,
  })),
);

const ProjectDetailPage = lazy(() =>
  import('@/features/projects/pages/ProjectDetailPage').then((module) => ({
    default: module.ProjectDetailPage,
  })),
);

const RequirementsPage = lazy(() =>
  import('@/features/requirements/pages/RequirementsPage').then((module) => ({
    default: module.RequirementsPage,
  })),
);

const RequirementEditPage = lazy(() =>
  import('@/features/requirements/pages/RequirementEditPage').then((module) => ({
    default: module.RequirementEditPage,
  })),
);
const RequirementCreatePage = lazy(() =>
  import('@/features/requirements/pages/RequirementCreatePage').then((module) => ({
    default: module.RequirementCreatePage,
  })),
);

const RequirementDetailPage = lazy(() =>
  import('@/features/requirements/pages/RequirementDetailPage').then((module) => ({
    default: module.RequirementDetailPage,
  })),
);

/** Indicateur affiché pendant le téléchargement d'une page différée. */
const RouteFallback = () => (
  <div className="grid place-items-center py-24" role="status" aria-live="polite">
    <SpinnerIcon className="text-navy-300 text-3xl" />
    <span className="sr-only">Chargement de la page…</span>
  </div>
);

/**
 * Table de routage.
 *
 * Quatre gabarits, un par contexte :
 *   AuthLayout  écrans de connexion — plein écran, sans navigation
 *   AppLayout   application connectée — en-tête permanent, derrière `RequireAuth`
 *   FullscreenLayout éditeurs connectés — pleine largeur, sans en-tête global
 *   RootLayout  pages hors parcours (404)
 */
export const AppRouter = () => (
  <Routes>
    <Route path={ROUTES.home} element={<Navigate to={ROUTES.dashboard} replace />} />

    <Route element={<AuthLayout />}>
      <Route path={ROUTES.login} element={<LoginPage />} />
    </Route>

    <Route
      element={
        <RequireAuth>
          <FullscreenLayout />
        </RequireAuth>
      }
    >
      <Route
        path={ROUTES.candidateCvs(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <CandidateCvPage />
          </Suspense>
        }
      />
    </Route>

    {/* La garde enveloppe le gabarit, et non chaque page : une route ajoutée
        ici est protégée d'office. L'oubli inverse — protéger page par page —
        est la façon la plus courante de laisser fuiter un écran. */}
    <Route
      element={
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      }
    >
      <Route
        path={ROUTES.account}
        element={
          <Suspense fallback={<RouteFallback />}>
            <AccountPage />
          </Suspense>
        }
      />
      <Route path="/parametres/numerotation" element={<DocumentNumberingPage />} />
      <Route
        path={ROUTES.candidateTrash}
        element={
          <Suspense fallback={<RouteFallback />}>
            <CandidateTrashPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.settings}
        element={
          <Suspense fallback={<RouteFallback />}>
            <SettingsPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.preferences}
        element={
          <Suspense fallback={<RouteFallback />}>
            <PreferencesPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.holidays}
        element={
          <Suspense fallback={<RouteFallback />}>
            <HolidaysPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.holidayImport}
        element={
          <Suspense fallback={<RouteFallback />}>
            <HolidayImportPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.holidayCreate}
        element={
          <Suspense fallback={<RouteFallback />}>
            <HolidayFormPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.holidayEdit(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <HolidayFormPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.companyIdentity}
        element={
          <Suspense fallback={<RouteFallback />}>
            <CompanyIdentityPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.aiEmailHistory}
        element={
          <Suspense fallback={<RouteFallback />}>
            <AiEmailHistoryPage />
          </Suspense>
        }
      />
      <Route path={ROUTES.dashboard} element={<DashboardPage />} />
      <Route
        path="/parametres/acces/collaborateurs/nouveau"
        element={<AccessFormPage kind="user" createUser />}
      />
      <Route path="/parametres/acces" element={<AccessPage />} />
      <Route path="/parametres/acces/roles/nouveau" element={<AccessFormPage kind="role" />} />
      <Route path="/parametres/acces/roles/:id/modifier" element={<AccessFormPage kind="role" />} />
      <Route
        path="/parametres/acces/roles/:id/dupliquer"
        element={<AccessFormPage kind="role" clone />}
      />
      <Route path="/parametres/acces/equipes/nouvelle" element={<AccessFormPage kind="team" />} />
      <Route
        path="/parametres/acces/equipes/:id/modifier"
        element={<AccessFormPage kind="team" />}
      />
      <Route
        path="/parametres/acces/collaborateurs/:id/role"
        element={<AccessFormPage kind="user" />}
      />
      {/* `/societes/nouvelle` avant `/societes/:id` : React Router classe déjà
          ses routes par spécificité, mais l'ordre le dit au lecteur. */}
      <Route
        path={ROUTES.companyCreate}
        element={
          <Suspense fallback={<RouteFallback />}>
            <CompanyCreatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.company(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <CompanyDetailPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.contactCreate}
        element={
          <Suspense fallback={<RouteFallback />}>
            <ContactCreatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.contact(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <ContactDetailPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.companies}
        element={
          <Suspense fallback={<RouteFallback />}>
            <CompaniesContactsPage />
          </Suspense>
        }
      />
      <Route
        path="/imports/besoins/:id"
        element={
          <Suspense fallback={<RouteFallback />}>
            <RequirementImportDetailPage />
          </Suspense>
        }
      />
      <Route
        path="/candidats/imports"
        element={
          <Suspense fallback={<RouteFallback />}>
            <CvImportsPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.candidateImport}
        element={
          <Suspense fallback={<RouteFallback />}>
            <CvImportsPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.candidates}
        element={
          <Suspense fallback={<RouteFallback />}>
            <CandidatesPage />
          </Suspense>
        }
      />
      {/* `/actions/nouvelle` est déclarée avant `/actions` : l'ordre le dit au
          lecteur, même si React Router classe déjà ses routes par spécificité. */}
      {/* Une route de saisie par type : chaque document a son formulaire, avec
          ses champs et ses obligations. Elles ne croisent pas
          `/adv/documents/:id`, qui ne sert qu'à la lecture. */}
      <Route
        path="/achats"
        element={
          <Suspense fallback={<RouteFallback />}>
            <AdvPage />
          </Suspense>
        }
      />
      <Route
        path="/achats/propositions/nouvelle"
        element={
          <Suspense fallback={<RouteFallback />}>
            <PropositionCreatePage />
          </Suspense>
        }
      />
      <Route
        path="/achats/bons-de-commande/nouveau"
        element={
          <Suspense fallback={<RouteFallback />}>
            <OrderCreatePage />
          </Suspense>
        }
      />
      <Route
        path="/achats/contrats/nouveau"
        element={
          <Suspense fallback={<RouteFallback />}>
            <ContractCreatePage />
          </Suspense>
        }
      />
      <Route
        path="/achats/factures/nouvelle"
        element={
          <Suspense fallback={<RouteFallback />}>
            <InvoiceCreatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.advPropositionCreate}
        element={
          <Suspense fallback={<RouteFallback />}>
            <PropositionCreatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.advOrderCreate}
        element={
          <Suspense fallback={<RouteFallback />}>
            <OrderCreatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.advContractCreate}
        element={
          <Suspense fallback={<RouteFallback />}>
            <ContractCreatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.advPvaCreate}
        element={
          <Suspense fallback={<RouteFallback />}>
            <PvaCreatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.advInvoiceCreate}
        element={
          <Suspense fallback={<RouteFallback />}>
            <InvoiceCreatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.missionFinancial(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <MissionFinancialPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.actionEdit(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <ActionEditPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.positioning(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <PositioningPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.actionFinancial(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <ActionEditPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.advDocument(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <DocumentDetailPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.adv}
        element={
          <Suspense fallback={<RouteFallback />}>
            <AdvPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.actionCreate}
        element={
          <Suspense fallback={<RouteFallback />}>
            <ActionCreatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.timesheets}
        element={
          <Suspense fallback={<RouteFallback />}>
            <TimesheetsPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.timesheetSatisfactionSettings}
        element={
          <Suspense fallback={<RouteFallback />}>
            <TimesheetSatisfactionSettingsPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.timesheet(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <TimesheetDetailPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.timesheetSatisfaction(':id', ':stage')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <TimesheetSatisfactionPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.interviews}
        element={
          <Suspense fallback={<RouteFallback />}>
            <InterviewsPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.tasks}
        element={
          <Suspense fallback={<RouteFallback />}>
            <TasksPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.actions}
        element={
          <Suspense fallback={<RouteFallback />}>
            <ActionsPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.projects}
        element={
          <Suspense fallback={<RouteFallback />}>
            <ProjectsPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.project(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <ProjectDetailPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.requirements}
        element={
          <Suspense fallback={<RouteFallback />}>
            <RequirementsPage />
          </Suspense>
        }
      />
      {/* `/besoins/nouveau` est déclarée avant `/besoins/:id`. React Router
          classe ses routes par spécificité et choisirait déjà la bonne, mais
          l'ordre le dit au lecteur sans qu'il ait à connaître cette règle. */}
      <Route
        path={ROUTES.requirementCreate}
        element={
          <Suspense fallback={<RouteFallback />}>
            <RequirementCreatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.requirementEdit(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <RequirementEditPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.requirement(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <RequirementDetailPage />
          </Suspense>
        }
      />
      {/* `:id` est validé côté serveur (UUID) : une valeur invalide renvoie
          une 404 applicative plutôt qu'un écran vide. */}
      <Route
        path={ROUTES.cvContactCompletion(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <CvContactCompletionPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.candidateCvUpdate(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <CandidateCvUpdatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.candidate(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <CandidateDetailPage />
          </Suspense>
        }
      />
    </Route>

    <Route
      element={
        <RequireAuth>
          <AppLayout contentMode="workspace" />
        </RequireAuth>
      }
    >
      <Route
        path={ROUTES.prospection}
        element={
          <Suspense fallback={<RouteFallback />}>
            <ProspectionPage />
          </Suspense>
        }
      />

      {/* La messagerie partage ce gabarit large : la liste des destinataires
          d'un lancement et l'aperçu d'un segment ont besoin de la largeur. */}
      <Route
        path="/messagerie/sequences"
        element={
          <Suspense fallback={<RouteFallback />}>
            <SequencesPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.messaging}
        element={
          <Suspense fallback={<RouteFallback />}>
            <SequencesPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.messagingSequenceCreate}
        element={
          <Suspense fallback={<RouteFallback />}>
            <SequenceCreatePage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.messagingSequenceEdit(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <SequenceEditPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.messagingSequence(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <SequenceDetailPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.messagingRun(':id')}
        element={
          <Suspense fallback={<RouteFallback />}>
            <RunDetailPage />
          </Suspense>
        }
      />
    </Route>

    <Route element={<RootLayout />}>
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Routes>
);
